import {observer} from 'mobx-react';
import {ObjectSelect2, Panel, Spinner} from 'shared/components';
import {Table} from 'main/components';
import useModal from 'shared/hooks/useModal';
import TimesheetEntry from 'main/stores/time_tracking/TimesheetEntry';
import {withState} from 'shared/core';
import TimesheetEditorState from '../state/TimesheetEditorState';
import {useIsMobile} from 'shared/hooks';
import EditEntryBreakModal from './EditEntryBreakModal';
import TimeClock from './TimeClock';
import EditEntryNoteModal from './EditEntryNoteModal';
import ClockInLocationModal from './ClockInLocationModal';
import Totals from './Totals';
import TimesheetActions from './TimesheetActions';
import StartTime from './StartTime';
import EndTime from './EndTime';
import Date from './Date';
import Note from './Note';
import DimensionValue from './DimensionValue';
import Hours from './Hours';
import ManualHours from './ManualHours';
import ApplyShift from './ApplyShift';

const shiftColumn = {
  header: 'time_tracking.timesheet.Shift',
  width: 2,
  component: ApplyShift
};

const dimensionColumns = (dimensions, singleDimensionWidth) => dimensions.map(dimension => {
  const width = dimensions.length > 1 ? 3 : singleDimensionWidth;

  return {
    header: dimension.name,
    translateHeader: false,
    width: width,
    component: props => <DimensionValue {...props} dimension={dimension}/>
  };
});

const manualColumns = (dimensions, schedulingEnabled) => {
  const fillWidth = schedulingEnabled ? 5.75 : 7.75;

  return [
    {
      header: 'time_tracking.timesheet.Date',
      width: 2.5,
      component: Date
    },
    ...(schedulingEnabled ? [shiftColumn] : []),
    {
      header: 'time_tracking.timesheet.Hours',
      width: dimensions.length === 0 ? fillWidth : 1.5,
      component: ManualHours
    },
    ...dimensionColumns(dimensions, fillWidth - 1.5),
    {
      header: 'time_tracking.timesheet.Note',
      width: 0.75,
      component: Note
    }
  ];
};

const clockColumns = (dimensions, schedulingEnabled) => {
  const timeWidth = schedulingEnabled ? 1.5 : 2;
  const fillWidth = schedulingEnabled ? 3.25 : 4.25;

  return [
    {
      header: 'time_tracking.timesheet.Date',
      width: 2,
      component: Date
    },
    ...(schedulingEnabled ? [shiftColumn] : []),
    {
      header: 'time_tracking.timesheet.In',
      width: timeWidth,
      component: StartTime
    },
    {
      header: 'time_tracking.timesheet.Out',
      width: timeWidth,
      component: EndTime
    },
    {
      header: 'time_tracking.timesheet.Hours',
      width: dimensions.length === 0 ? fillWidth : 1,
      component: Hours
    },
    ...dimensionColumns(dimensions, fillWidth - 1),
    {
      header: 'time_tracking.timesheet.Note',
      width: 0.75,
      component: Note
    }
  ];
};

const TimesheetEditor = observer(({uiState, myTimesheet}) => {
  const editEntryNoteModalOpen = useModal(uiState, 'EditEntryNoteModal', 'editingEntry', TimesheetEntry);
  const editEntryBreakModalOpen = useModal(uiState, 'EditEntryBreakModal', 'editingEntry', TimesheetEntry);
  const clockInLocationModalOpen = useModal(uiState, 'ClockInLocationModal', 'viewingClockEvent');
  const {timesheet, payPeriod, payPeriods, employee, isLoadingEntries} = uiState;
  const isMobile = useIsMobile();

  if (isLoadingEntries) return <Panel><Spinner/></Panel>;

  return (
    <Panel.Stack loose>
      {timesheet.timeClockEnabled &&
        <TimeClock
          collectEmployeeLocation={timesheet.collectEmployeeLocation}
          canCreateEntries={timesheet.canCreateEntries}
          onClockEvent={() => uiState.loadEntries()}
          employee={employee}
          myTimesheet={myTimesheet}
          dimensions={timesheet.dimensions}
        />
      }
      <Panel>
        {payPeriods ? <div className='flex flex-wrap justify-content-between align-items-end mb3'>
          <ObjectSelect2
            value={payPeriod}
            items={payPeriods}
            display='effectiveDateView'
            label='time_tracking.timesheet.Pay Period'
            onChange={value => uiState.onPayPeriodSelected(value)}
          />
          <TimesheetActions uiState={uiState}/>
        </div> :
          <Panel.Header title={employee.name}>
            <TimesheetActions uiState={uiState}/>
          </Panel.Header>
        }
        <Totals uiState={uiState}/>
        <Table
          uiState={uiState}
          models={timesheet.rows()}
          columns={timesheet.manuallyEnterHours ?
            manualColumns(timesheet.dimensions, timesheet.schedulingEnabled) :
            clockColumns(timesheet.dimensions, timesheet.schedulingEnabled)}
          customLinks={model => uiState.customLinksFor(model)}
          onlyShowCustomLinks
          onRemove={model => timesheet.removeEntry(model.data, payPeriod)}
          horizontalScroll={isMobile || timesheet.dimensions.length > 1}
        />
        <EditEntryNoteModal
          uiState={uiState}
          isOpen={editEntryNoteModalOpen}
        />
        <EditEntryBreakModal
          uiState={uiState}
          isOpen={editEntryBreakModalOpen}
        />
        <ClockInLocationModal
          uiState={uiState}
          isOpen={clockInLocationModalOpen}
        />
      </Panel>
    </Panel.Stack>
  );
});

export default withState(TimesheetEditor, TimesheetEditorState);
